import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Lee el cuerpo JSON de forma segura (algunos errores pueden venir vacíos)
async function readJson(response) {
  try {
    return await response.json();
  } catch (e) {
    return {};
  }
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isPremium: false,
      isAdmin: false,
      token: null,
      hydrated: false, // true cuando ya confirmamos la sesión con el servidor (evita parpadeo del muro)

      login: async (username, password) => {
        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ username, password }),
          });

          const data = await readJson(response);

          if (!response.ok) {
            return {
              success: false,
              error: data.error || 'No se pudo iniciar sesión',
              needsVerification: !!data.needs_verification,
              status: response.status,
            };
          }

          set({
            user: data.user,
            isAuthenticated: true,
            isPremium: (data.user && data.user.premium) || false,
            isAdmin: (data.user && data.user.admin) || false,
            token: data.token,
          });

          return { success: true };
        } catch (error) {
          return { success: false, error: 'No se pudo conectar con el servidor' };
        }
      },

      // Rehidrata desde la sesión del servidor (cookie). Útil tras login con Google
      // y para mantener rol/premium al día (el servidor es la autoridad).
      hydrate: async () => {
        try {
          const r = await fetch('/api/auth/me', { credentials: 'include' });
          if (!r.ok) {
            set({ user: null, isAuthenticated: false, isPremium: false, isAdmin: false, token: null, hydrated: true });
            return false;
          }
          const u = await readJson(r);
          set({
            user: u,
            isAuthenticated: true,
            isPremium: !!u.premium,
            isAdmin: !!u.admin,
            hydrated: true,
          });
          return true;
        } catch (e) {
          set({ hydrated: true });
          return false;
        }
      },

      logout: () => {
        // Limpia también la sesión del servidor (best-effort)
        fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        set({
          user: null,
          isAuthenticated: false,
          isPremium: false,
          isAdmin: false,
          token: null,
        });
      },

      register: async ({ email, password }) => {
        try {
          const response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          const data = await readJson(response);
          if (!response.ok) {
            return { success: false, error: data.error || 'No se pudo registrar' };
          }
          return { success: true, message: data.message };
        } catch (error) {
          return { success: false, error: 'No se pudo conectar con el servidor' };
        }
      },

      resendVerification: async (email) => {
        try {
          const response = await fetch('/api/auth/resend-verification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await readJson(response);
          return { success: response.ok, message: data.message, error: data.error };
        } catch (error) {
          return { success: false, error: 'No se pudo conectar con el servidor' };
        }
      },

      verifyEmail: async (token) => {
        try {
          const response = await fetch('/api/auth/verify-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
          const data = await readJson(response);
          return { success: response.ok, message: data.message, error: data.error };
        } catch (error) {
          return { success: false, error: 'No se pudo conectar con el servidor' };
        }
      },

      forgotPassword: async (email) => {
        try {
          const response = await fetch('/api/auth/forgot-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await readJson(response);
          return { success: response.ok, message: data.message, error: data.error };
        } catch (error) {
          return { success: false, error: 'No se pudo conectar con el servidor' };
        }
      },

      resetPassword: async (token, password) => {
        try {
          const response = await fetch('/api/auth/reset-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password }),
          });
          const data = await readJson(response);
          return { success: response.ok, message: data.message, error: data.error };
        } catch (error) {
          return { success: false, error: 'No se pudo conectar con el servidor' };
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isPremium: state.isPremium,
        isAdmin: state.isAdmin,
        token: state.token,
      }),
    }
  )
);
